import Keycloak from "keycloak-js";

const _kc = new Keycloak("/keycloak.json");
let idleTimer;
let refreshTimer;
let idleTimeout = 14 * 60 * 1000; // 14 minutes
let refreshInterval = 60 * 1000; // 1 minute
let refreshFailureCount = 0;
const maxRefreshFailuresBeforeLogout = 3;

const events = ["mousemove", "keydown", "scroll", "click"];

const clearClientSession = () => {
  try {
    sessionStorage.removeItem("token");
  } catch (e) {
    console.log("sessionStorage clear error:", e);
  }
};

const resetIdleTimer = () => {
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    console.log("User idle for 2 minutes → logging out");
    doLogout();
  }, idleTimeout);
};

const setupActivityListeners = () => {
  events.forEach((event) => {
    window.addEventListener(event, resetIdleTimer);
  });
};

const removeActivityListeners = () => {
  events.forEach((event) => {
    window.removeEventListener(event, resetIdleTimer);
  });
};
/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback) => {
  _kc
    .init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri:
        window.location.origin + "/silent-check-sso.html",
      pkceMethod: "S256",
    })
    .then((authenticated) => {
      if (!authenticated) {
        console.log("user is not authenticated..!");
      }
      onAuthenticatedCallback();

      setupActivityListeners();
      resetIdleTimer();

      // Periodic token refresh if user is active
      refreshTimer = setInterval(() => {
        _kc
          .updateToken(120) // refresh if token expires in next 30s
          .then((refreshed) => {
            refreshFailureCount = 0;
            if (refreshed) {
              console.log("Token was refreshed successfully");
            } else {
              console.log("Token still valid, no refresh needed");
            }
          })
          .catch(() => {
            // Retry with a forced refresh before deciding to end session.
            _kc
              .updateToken(0)
              .then(() => {
                refreshFailureCount = 0;
                console.log("Token refresh recovered on forced retry");
              })
              .catch(() => {
                refreshFailureCount += 1;
                if (refreshFailureCount >= maxRefreshFailuresBeforeLogout) {
                  console.log("Token refresh failed repeatedly → logging out");
                  doLogout();
                } else {
                  console.log(
                    `Token refresh failed (${refreshFailureCount}/${maxRefreshFailuresBeforeLogout}), will retry`
                  );
                }
              });
          });
      }, refreshInterval);
    })
    .catch(console.error);

  _kc.onAuthLogout = () => {
    console.log("Keycloak triggered logout");
    clearClientSession();
    cleanupAfterLogout();
  };
};

const cleanupAfterLogout = () => {
  clearTimeout(idleTimer);
  clearInterval(refreshTimer);
  removeActivityListeners();
};

const doLogin = _kc.login;

const doLogout = () => {
  refreshFailureCount = 0;
  clearClientSession();
  cleanupAfterLogout();
  _kc.logout();
};

const getToken = () => _kc.token;

const isLoggedIn = () => !!_kc.token;

const updateToken = (successCallback) =>
  _kc.updateToken(5).then(successCallback).catch(doLogin);

const getUsername = () => _kc.tokenParsed?.preferred_username;
const getWelcomeName = () => _kc.tokenParsed?.given_name;
const getEmail = () => _kc.tokenParsed?.email;
const getUserId = () => _kc.tokenParsed?.sub;

const hasRole = (roles) => roles.some((role) => _kc.hasRealmRole(role));

const UserService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  getUsername,
  getWelcomeName,
  getEmail,
  getUserId,
  hasRole,
};

export default UserService;
